(ns arthur.flow.source
  "Retained analysis: three raw source blocks and an optional addressed interior
  measurement block. Everything after this boundary can run without PNGs or
  MediaPipe. Mouth crops retain pixels for settings that change the measurement."
  (:require [arthur.domain.landmarks :as lm]
            [arthur.domain.wire :as wire]
            [arthur.flow.address :as address]
            [arthur.flow.measure.interior :as interior]))

(def roles ["source/dense" "source/detected" "source/crops"])

(defn- interior-address [analysis subject settings frames]
  (let [verts (:teeth-verts settings)
        stride (+ 2 (* 2 verts))]
    (address/block {:role "source/interior" :analysis analysis
                    :params settings
                    :tracks ["contrast" "area" "contour"]
                    :features [subject] :observation nil
                    :layout {:type "float64" :frames frames :verts verts
                             :stride stride}})))

(defn interior-key [analysis subject settings frames]
  (:key (interior-address analysis subject settings frames)))

(defn interior-block
  "Retain one subject's pixel measurements before any head-local transform or
  smoothing."
  [analysis subject settings measures]
  (let [frames (count measures)
        {:keys [key descriptor]}
        (interior-address analysis subject settings frames)
        verts (:teeth-verts settings)
        stride (+ 2 (* 2 verts))
        data (js/Float64Array. (* frames stride))]
    (doseq [f (range frames)]
      (let [{:keys [contrast area contour]} (nth measures f)
            at (* f stride)]
        (aset data at (or contrast 0))
        (aset data (inc at) (or area 0))
        (if contour
          (do
            (when-not (= verts (count contour))
              (throw (ex-info "interior contour vertex count disagrees with settings"
                              {:frame f :expected verts :actual (count contour)})))
            (doseq [i (range verts)]
              (aset data (+ at 2 (* 2 i)) (:x (nth contour i)))
              (aset data (+ at 3 (* 2 i)) (:y (nth contour i)))))
          (aset data (+ at 2) js/NaN))))
    {:role "source/interior" :key key :descriptor descriptor :data data}))

(defn unpack-interior [^js response settings frames]
  (let [descriptor (js->clj (js/JSON.parse (.-descriptor response))
                            :keywordize-keys true)
        layout (:layout descriptor)
        data (wire/typed (:type layout) (.-data response))
        verts (:teeth-verts settings)
        stride (+ 2 (* 2 verts))]
    (when-not (and (= "source/interior" (:role descriptor))
                   (= frames (:frames layout)) (= verts (:verts layout))
                   (= stride (:stride layout))
                   (= (* frames stride) (.-length data)))
      (throw (ex-info "saved interior block has the wrong layout"
                      {:layout layout :frames frames :verts verts})))
    (mapv (fn [f]
            (let [at (* f stride)]
              {:contrast (aget data at) :area (aget data (inc at))
               :contour (when-not (js/Number.isNaN (aget data (+ at 2)))
                          (mapv (fn [i]
                                  {:x (aget data (+ at 2 (* 2 i)))
                                   :y (aget data (+ at 3 (* 2 i)))})
                                (range verts)))}))
          (range frames))))

(defn measure-crop
  "One mouth crop's interior, or the empty measurement for a frame with no face.

  Singular because the caller that matters measures a crop the moment it has one,
  while the rest of the frame's pixels are still on the canvas — see
  `events/footage`. Measuring all of them afterwards is the same arithmetic and
  ten seconds of a frozen page."
  [params crop]
  (if crop
    (interior/measure params (:box crop) #js {:data (:data crop)})
    {:contour nil :contrast 0 :area 0 :debug nil}))

(defn- named [role analysis subject tracks layout data]
  (merge (address/block {:role role :analysis analysis :params {}
                         :tracks tracks :features [subject] :observation nil
                         :layout layout})
         {:role role :data data}))

(defn pack
  "One SUBJECT's dense landmarks, mask, crops and optional pixel measurements ->
  source blocks.

  `:interior-settings` is not optional when `:interior` is present: the block is
  addressed BY those knobs, and defaulting them here would name a block after
  settings its bytes did not come from — a content address that lies, which is the
  one failure the key exists to prevent.

  `subject` is in every block's descriptor, and it has to be. These four blocks
  carry no feature — they are the raw analysis, upstream of any part — so two
  faces tracked in one video would otherwise produce different bytes under one
  identical key, and the second face's landmarks would replace the first's in the
  store. That is the same failure `freeze/head-part` guards against one stage
  later, for the same reason."
  [analysis subject {:keys [dense detected crops interior interior-settings]}]
  (let [frames (count dense)
        points (count (first dense))]
    (when-not (and (pos? frames) (pos? points)
                   (= frames (count detected) (count crops))
                   (or (nil? interior) (= frames (count interior)))
                   (every? #(= points (count %)) dense))
      (throw (ex-info "source tracks must have the same frame and landmark counts"
                      {:frames frames :points points})))
    (let [landmarks (js/Float64Array. (* frames points 3))
          mask (js/Uint8Array. frames)
          lengths (mapv #(if % (.-length ^js (:data %)) 0) crops)
          offsets (vec (reductions + 0 lengths))
          pixels (js/Uint8Array. (max 1 (peek offsets)))
          boxes (mapv (fn [crop]
                        (when-let [box (:box crop)]
                          (mapv box [:x :y :w :h])))
                      crops)]
      (doseq [f (range frames)]
        (aset mask f (if (nth detected f) 1 0))
        (doseq [i (range points)]
          (let [{:keys [x y z]} (nth (nth dense f) i)
                base (* 3 (+ (* f points) i))]
            (aset landmarks base x)
            (aset landmarks (inc base) y)
            (aset landmarks (+ base 2) z)))
        (when-let [crop (nth crops f)]
          (.set pixels (:data crop) (nth offsets f))))
      (cond-> {"source/dense"
               (named "source/dense" analysis subject ["landmarks"]
                      {:type "float64" :frames frames :points points
                       :stride (* 3 points)}
                      landmarks)
               "source/detected"
               (named "source/detected" analysis subject ["detected"]
                      {:type "uint8" :frames frames :stride 1} mask)
               "source/crops"
               (named "source/crops" analysis subject ["rgba"]
                      {:type "uint8" :frames frames :boxes boxes :offsets offsets}
                      pixels)}
        interior (assoc "source/interior"
                        (interior-block
                         analysis subject
                         (or interior-settings
                             (throw (ex-info "an interior block needs the settings its measurements were taken at"
                                             {:frames (count interior)})))
                         interior))))))

(defn pack-subjects
  "Every tracked subject's source blocks: `{subject {role block}}`.

  One video, one analysis, one set of blocks PER FACE. They are separate blocks
  and not one block with a subject axis, because a face can be re-measured on its
  own — a knob dragged on the second person re-reads the second person's
  landmarks — and because that is what makes each one's content address name
  exactly the bytes it covers."
  [analysis by-subject]
  (into {} (map (fn [[subject inputs] ] [subject (pack analysis subject inputs)]))
        by-subject))

(defn- flatten-blocks
  "`{subject {role block}}` -> one flat seq, in a stable order."
  [by-subject wanted]
  (for [[_ blocks] (sort-by (comp str key) by-subject)
        role wanted
        :when (contains? blocks role)]
    (get blocks role)))

(defn block-keys
  "The keys of every subject's source blocks, in the order they upload."
  [by-subject]
  (mapv :key (flatten-blocks by-subject roles)))

(defn wire-blocks [by-subject]
  (into-array
   (map (fn [{:keys [key descriptor data]}]
          #js {:key key :descriptor descriptor :data (wire/base64 data)})
        (flatten-blocks by-subject roles))))

(defn upload-blocks [by-subject]
  (into-array
   (map (fn [{:keys [key descriptor data]}]
          #js {:key key :descriptor descriptor :data data})
        (flatten-blocks by-subject (conj roles "source/interior")))))

(defn- unpack-one
  "One subject's three blocks -> its inputs for measurement and freeze."
  [by-role [width height]]
  (let [_ (when-not (= (set roles) (set (keys by-role)))
            (throw (ex-info "analysis is missing source blocks" {:roles (keys by-role)})))
        {:keys [layout data]} (get by-role "source/dense")
        frames (:frames layout)
        points (:points layout)
        mask (:data (get by-role "source/detected"))
        crop-data (:data (get by-role "source/crops"))
        crop-layout (:layout (get by-role "source/crops"))]
    (when-not (and (= (* frames points 3) (.-length data))
                   (= frames (.-length mask) (:frames crop-layout))
                   (= frames (count (:boxes crop-layout)))
                   (= (inc frames) (count (:offsets crop-layout))))
      (throw (ex-info "source block layouts disagree" {:frames frames :points points})))
    (let [dense (mapv (fn [f]
                        (mapv (fn [i]
                                (let [base (* 3 (+ (* f points) i))]
                                  {:x (aget data base) :y (aget data (inc base))
                                   :z (aget data (+ base 2))}))
                              (range points)))
                      (range frames))
          detected (mapv #(pos? (aget mask %)) (range frames))
          crops (mapv
                 (fn [f]
                   (when-let [[x y w h] (nth (:boxes crop-layout) f)]
                     (let [start (nth (:offsets crop-layout) f)
                           end (nth (:offsets crop-layout) (inc f))
                           shape (mapv #(nth (nth dense f) %) lm/LIPS-INNER)]
                       (when-not (= (* 4 w h) (- end start))
                         (throw (ex-info "mouth crop byte count disagrees with its box"
                                         {:frame f :expected (* 4 w h) :actual (- end start)})))
                       {:box {:x x :y y :w w :h h :shape shape
                              :source-width width :source-height height}
                        :data (js/Uint8ClampedArray. (.-buffer crop-data) start (- end start))})))
                 (range frames))]
      {:dense dense :detected detected :crops crops :dimensions [width height]
       :missing (count (remove true? detected))
       :first-real (first (keep-indexed (fn [i present?] (when present? i)) detected))})))

(defn unpack
  "Every subject's block-detail responses -> `{:subjects {id inputs}}`.

  WHICH SUBJECT A BLOCK BELONGS TO IS READ OFF ITS DESCRIPTOR, not off the order
  the server returned it in. The descriptor is the thing the key is a hash of, so
  it is the only statement about a block that cannot have drifted from its bytes."
  [^js responses dimensions]
  (let [parsed (map (fn [^js response]
                      (let [descriptor (js->clj (js/JSON.parse (.-descriptor response))
                                                :keywordize-keys true)]
                        {:subject (keyword (first (:features descriptor)))
                         :role (:role descriptor)
                         :block {:layout (:layout descriptor)
                                 :data (wire/typed (get-in descriptor [:layout :type])
                                                   (.-data response))}}))
                    (array-seq responses))]
    (when (some (comp nil? :subject) parsed)
      (throw (ex-info "a source block does not say which subject it came from"
                      {:roles (mapv :role parsed)})))
    {:subjects
     (into {}
           (map (fn [[subject entries]]
                  [subject (unpack-one (into {} (map (juxt :role :block)) entries)
                                       dimensions)]))
           (group-by :subject parsed))}))
